import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Sparkles, Copy, Check, Download, Link as LinkIcon, Twitter, Facebook, ImageOff,
} from "lucide-react";

/**
 * SocialKit — Black Friday launch kit for ranchers & ambassadors.
 * Ready-made branded graphics (square / story / wide banner) + copy-paste
 * captions with the sharer's personal referral link baked in.
 */

const SIZES = [
  { id: "sq", label: "Square", dims: "1080×1080", hint: "Feed post" },
  { id: "st", label: "Story", dims: "1080×1920", hint: "Story / Reel" },
  { id: "bn", label: "Banner", dims: "Wide", hint: "FB / X header" },
];

// Caption templates use {link} as the personal-link placeholder.
const DESIGNS = [
  {
    id: "launch",
    title: "Launching Black Friday",
    blurb: "Announce the Nov 27 launch day.",
    caption:
      "It's almost here. 🥩 The Farm To You launches Black Friday, November 27 — 100% American-raised beef & chicken shipped straight from family ranches to your door. Lock in your founding membership now 👉 {link}\n\n#thefarmtoyou #blackfriday #grassfed #americanraised #realfood",
  },
  {
    id: "shipping",
    title: "Free Shipping for Members",
    blurb: "The member perk everyone loves.",
    caption:
      "Members never pay for shipping. 📦 Real farm-direct beef & chicken from American ranches, delivered free to your door with The Farm To You. Join with my link 👉 {link}\n\n#thefarmtoyou #freeshipping #grassfedbeef #farmtotable",
  },
  {
    id: "seats",
    title: "Limited Founding Seats",
    blurb: "Create urgency before launch.",
    caption:
      "Founding seats are limited. ⏳ Get in before The Farm To You launches Black Friday and lock in your founding-member perks. Claim your spot 👉 {link}\n\n#thefarmtoyou #foundingmember #americanranchers #grassfed",
  },
  {
    id: "plans",
    title: "Membership Plans",
    blurb: "$24.99/mo or $150/yr.",
    caption:
      "Good meat, simple membership. 🐄 $24.99/month or $150/year gets you farm-direct American beef & chicken with free member shipping. Join The Farm To You 👉 {link}\n\n#thefarmtoyou #membership #grassfedbeef #realfood",
  },
  {
    id: "ranchers",
    title: "Support Local Ranchers",
    blurb: "The mission message.",
    caption:
      "Every order supports a local American rancher. 🇺🇸 The Farm To You connects you straight to family farms — no middlemen, no mystery meat. Join the movement 👉 {link}\n\n#thefarmtoyou #supportlocal #americanranchers #farmtotable",
  },
  {
    id: "farmdirect",
    title: "Know Your Rancher",
    blurb: "Transparency & trust.",
    caption:
      "Know your rancher. Know your meat. 🥩 The Farm To You ships 100% American-raised beef & chicken from family farms right to your door. See for yourself 👉 {link}\n\n#thefarmtoyou #knowyourfarmer #grassfed #realfood",
  },
];

function CopyBtn({ text, label = "Copy", testId, disabled, full }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setDone(true);
    setTimeout(() => setDone(false), 1600);
  };
  return (
    <button
      type="button"
      onClick={copy}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 text-xs border rounded-full px-3 py-1.5 transition-colors disabled:opacity-40 ${done ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-stone-300 hover:border-[var(--ff-primary)] text-[var(--ff-text-secondary)]"} ${full ? "w-full" : ""}`}
      data-testid={testId}
    >
      {done ? <Check size={13} /> : <Copy size={13} />}
      {done ? "Copied" : label}
    </button>
  );
}

export default function SocialKit() {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/social-kit/me")
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load your social kit."));
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = useMemo(
    () => (info?.share_path ? `${origin}${info.share_path}` : ""),
    [info, origin]
  );

  const captionFor = (tpl) => tpl.replace("{link}", shareUrl || "thefarmtoyou.com");

  return (
    <div data-testid="social-kit">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles size={20} className="text-[var(--ff-primary)]" />
        <h2 className="font-display text-2xl text-[var(--ff-primary)]">Black Friday social kit</h2>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1 max-w-2xl">
        Ready-made launch-day graphics and captions. Download a graphic, copy the matching caption
        (your personal link is already baked in), and post to drive early signups.
      </p>

      {err && (
        <div className="mt-6 card-flat p-6 text-center text-rose-700 flex items-center justify-center gap-2" data-testid="social-kit-error">
          <ImageOff size={16} /> {err}
        </div>
      )}

      {/* Personal link */}
      <div className="mt-6 card-flat p-5 sm:p-6" data-testid="social-kit-link-card">
        <div className="flex items-center gap-2 text-[var(--ff-primary)]">
          <LinkIcon size={16} />
          <p className="overline">Your personal share link</p>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
          <code className="flex-1 truncate rounded-lg bg-stone-100 px-4 py-3 font-mono text-sm border border-stone-200" data-testid="social-kit-url">
            {shareUrl || "Loading your link…"}
          </code>
          <CopyBtn text={shareUrl} label="Copy link" testId="social-kit-copy-link" disabled={!shareUrl} />
        </div>
        <p className="mt-2 text-xs text-[var(--ff-text-muted)]">
          {info?.is_ambassador
            ? "Signups through this link auto-apply your ambassador code — you earn 10% commission."
            : "Friends who join through this link get $10 off, and you earn a $10 credit when they become a member."}
        </p>
      </div>

      {/* Design grid */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="social-kit-grid">
        {DESIGNS.map((d) => {
          const caption = captionFor(d.caption);
          const twitterShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(d.caption.replace("{link}", ""))}&url=${encodeURIComponent(shareUrl)}`;
          const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          return (
            <div key={d.id} className="card-flat overflow-hidden flex flex-col" data-testid={`social-design-${d.id}`}>
              <div className="relative aspect-square bg-stone-100">
                <img
                  src={`/assets/social-kit/sq_${d.id}.jpg`}
                  alt={d.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-display text-lg text-[var(--ff-primary)]">{d.title}</h3>
                <p className="text-xs text-[var(--ff-text-muted)] mt-0.5">{d.blurb}</p>

                {/* Download buttons */}
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)] font-semibold mb-1.5">Download</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SIZES.map((s) => (
                      <a
                        key={s.id}
                        href={`/assets/social-kit/${s.id}_${d.id}.jpg`}
                        download={`farm-to-you-${d.id}-${s.id}.jpg`}
                        title={`${s.label} · ${s.dims} · ${s.hint}`}
                        className="inline-flex items-center gap-1 text-xs border border-stone-300 hover:border-[var(--ff-primary)] rounded-full px-2.5 py-1.5 transition-colors text-[var(--ff-text-secondary)]"
                        data-testid={`social-dl-${d.id}-${s.id}`}
                      >
                        <Download size={12} /> {s.label}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Caption */}
                <div className="mt-4 flex-1">
                  <p className="text-[11px] uppercase tracking-wider text-[var(--ff-text-muted)] font-semibold mb-1.5">Caption</p>
                  <p className="text-xs text-[var(--ff-text-secondary)] whitespace-pre-line leading-relaxed bg-stone-50 border border-stone-200 rounded-lg p-3 max-h-36 overflow-y-auto" data-testid={`social-caption-${d.id}`}>
                    {caption}
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-3 flex items-center gap-1.5">
                  <CopyBtn text={caption} label="Copy caption" testId={`social-copy-${d.id}`} disabled={!shareUrl} full />
                  <a
                    href={twitterShare}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center border border-stone-300 hover:border-[var(--ff-primary)] rounded-full p-1.5 transition-colors text-[var(--ff-text-secondary)]"
                    title="Share on X"
                    data-testid={`social-x-${d.id}`}
                  >
                    <Twitter size={13} />
                  </a>
                  <a
                    href={fbShare}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center border border-stone-300 hover:border-[var(--ff-primary)] rounded-full p-1.5 transition-colors text-[var(--ff-text-secondary)]"
                    title="Share on Facebook"
                    data-testid={`social-fb-${d.id}`}
                  >
                    <Facebook size={13} />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-[var(--ff-text-muted)]">
        Tip: on mobile, download the Story size to post to Instagram or Facebook Stories, then paste the caption into your feed post.
      </p>
    </div>
  );
}
